"use client";

import { useEffect, useState } from "react";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const { LOCALE_COOKIE, LANGUAGES, normalizeLocale, languageLabel } = require("@calcom/features/auth/lib/leadya-locale.cjs");

type Language = { code: string; name: string };

/** Lets a booking page visitor pick one of the LeadYa languages; the server renders the page in the chosen locale. */
export default function LeadyaLanguageSwitcher({ locale: rendered }: { locale?: string }) {
  // Without an explicit locale the document language is read, so the selector appears after hydration.
  // An empty value means the page uses a native locale outside the LeadYa list (for example es-419).
  const [current, setCurrent] = useState<string | null>(null);
  useEffect(() => {
    setCurrent(normalizeLocale(rendered ?? document.documentElement.lang) ?? "");
  }, [rendered]);
  if (current === null) return null;
  const choose = (value: string) => {
    const chosen = normalizeLocale(value);
    if (!chosen || chosen === current) return;
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${LOCALE_COOKIE}=${encodeURIComponent(chosen)}; Path=/; Max-Age=31536000; SameSite=Lax${secure}`;
    window.location.reload();
  };
  const label = languageLabel(current);
  return (
    <div
      data-leadya-language-switcher=""
      style={{ position: "fixed", top: 12, right: 12, zIndex: 60 }}
      className="bg-default border-subtle text-default rounded-md border shadow-sm">
      <label style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", fontSize: 13 }}>
        <span aria-hidden="true">🌐</span>
        <select
          aria-label={label}
          value={current}
          onChange={(event) => choose(event.target.value)}
          className="bg-default text-default"
          style={{ border: 0, fontSize: 13, cursor: "pointer" }}>
          {current === "" && (
            <option value="" disabled>
              {label}
            </option>
          )}
          {(LANGUAGES as Language[]).map((language) => (
            <option key={language.code} value={language.code} lang={language.code}>
              {language.name}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
